import { ArrowsLeftRightIcon } from "@phosphor-icons/react/dist/ssr/ArrowsLeftRight";
import { ArrowUpRightIcon } from "@phosphor-icons/react/dist/ssr/ArrowUpRight";
import { CalendarBlankIcon } from "@phosphor-icons/react/dist/ssr/CalendarBlank";
import { CertificateIcon } from "@phosphor-icons/react/dist/ssr/Certificate";
import { ReceiptIcon } from "@phosphor-icons/react/dist/ssr/Receipt";
import {
  differenceInDays,
  format,
  formatDistanceToNow,
  isFuture,
  isPast,
  sub,
} from "date-fns";

import { useProfile, useTheme } from "@up/data";
import { UPLink } from "@up/ui";

import { cohortToSwitch } from "./cohort-switch-store";
import { LineItem } from "../line-item";

import type { TEnrollment, TLineItem, TOrder } from "@up/data";

const formatDate = function (
  date: Date | string,
  pattern = "MMMM d, yyy",
  subtract = {},
) {
  return format(sub(new Date(date), subtract), pattern);
};

export const CourseInfo = ({
  compact = false,
  course,
  isGuardian = false,
  enrollments = [],
}: {
  course: TLineItem & { order: TOrder };
  compact?: boolean;
  isGuardian?: boolean;
  enrollments?: TEnrollment[];
}) => {
  const theme = useTheme();
  const profile = useProfile();
  const StartDate = new Date(course.startDate);
  const EndDate = new Date(course.endDate);
  const ApplicationDeadline = new Date(course.applicationDeadline);
  const startInFuture = isFuture(StartDate);
  const ableToSwitch = isFuture(ApplicationDeadline);
  const startInPast = isPast(StartDate);
  const startDistanceInDays = differenceInDays(StartDate, new Date());
  const only = startDistanceInDays < 60;
  const endInFuture = isFuture(EndDate);
  const currentlyRunning = startInPast && endInFuture;
  const certificate = profile.contact?.certificates?.find(
    (cert) => cert.cohortId === course._id,
  );
  const hasEnrollment = enrollments.some(
    (enrollment) => enrollment.order._id === course.order._id,
  );

  if (!hasEnrollment) {
    return null;
  }

  return (
    <div>
      <div className="mb-10">
        <h2 className={`${theme.heading3Class}`} key={course._id}>
          {course.catalogueItem.name}
        </h2>
        <p className="mb-4 opacity-60">{course.name}</p>
        <LineItem
          compact={compact}
          as={UPLink}
          Icon={ReceiptIcon}
          href={`/orders/${course.order._id}`}
        >
          <span>
            <span className="text-sm uppercase opacity-75">
              Purchased: {formatDate(course.order.createdAt)}
            </span>
            <br />
            <span className="underline">View order details</span>
          </span>
        </LineItem>

        {startInFuture ? (
          <>
            <LineItem compact={compact} Icon={CalendarBlankIcon}>
              <span>
                <span className="text-sm uppercase opacity-75">
                  Starts {course.formattedStartDate}
                </span>
                <br />
                {only
                  ? `Only ${formatDistanceToNow(StartDate)} away!`
                  : `${formatDistanceToNow(StartDate)} away`}
              </span>
            </LineItem>
            {ableToSwitch ? (
              <LineItem
                as="button"
                compact={compact}
                Icon={ArrowsLeftRightIcon}
                onClick={() => {
                  const { order, ...lineItem } = course;
                  cohortToSwitch.set(lineItem);
                }}
              >
                <span>
                  <span className="text-sm uppercase opacity-75">
                    Need to reschedule?
                  </span>
                  <br />

                  <span className="underline">Switch to another cohort</span>
                </span>
              </LineItem>
            ) : null}
          </>
        ) : null}

        {currentlyRunning ? (
          <LineItem
            as={UPLink}
            compact={compact}
            href={theme.lmsURL}
            Icon={ArrowUpRightIcon}
            target="_blank"
            rel="noreferrer"
          >
            <span>
              <span className="text-sm uppercase opacity-75">
                Started {formatDate(StartDate)}
              </span>
              <br />
              <span className="underline">Go to course</span>
            </span>
          </LineItem>
        ) : null}

        {certificate ? (
          <LineItem
            as={UPLink}
            href={certificate.certificateURL}
            target="_blank"
            rel="noreferrer"
            compact={compact}
            Icon={CertificateIcon}
          >
            <span>
              <span className="text-sm uppercase opacity-75">
                Certificate Earned!
              </span>
              <br />
              <span className="underline">View Certificate</span>
            </span>
          </LineItem>
        ) : null}
      </div>
    </div>
  );
};
